import { useAuth } from "@/lib/auth";
import { getIcConfig } from "@/lib/network";
import {
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
	amountToNat,
} from "@/lib/protocol-actor";
import { approveIcrcSpender, unwrapResult } from "@motokomaker/ic-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const GAS_DONATE_PRESETS = [1, 5, 10] as const;

export type GasSupporterUi = {
	id: number;
	donor: string;
	amountIcp: number;
	message: string;
	donatedAtNs: number;
	messageAtNs: number | null;
};

function formatNsDate(ns: number): string {
	if (!Number.isFinite(ns) || ns <= 0) return "—";
	const ms = ns > 1e15 ? ns / 1e6 : ns;
	try {
		return new Date(ms).toLocaleString();
	} catch {
		return "—";
	}
}

export { formatNsDate };

export function useGasSupporters(offset = 0, limit = 20) {
	return useQuery({
		queryKey: ["v2", "gasSupporters", offset, limit],
		queryFn: async (): Promise<{ events: GasSupporterUi[]; total: number }> => {
			const actor = await getV2ProtocolActor();
			if (!actor || !hasProtocolEnv()) return { events: [], total: 0 };
			if (typeof actor.getGasSupporters !== "function") {
				return { events: [], total: 0 };
			}
			const page = await actor.getGasSupporters(BigInt(offset), BigInt(limit));
			const events: GasSupporterUi[] = (page.events ?? []).map((e) => {
				const msgAt = e.messageAt;
				return {
					id: Number(e.id),
					donor: e.donor.toText(),
					amountIcp: natToAmount(e.amountE8),
					message: e.message ?? "",
					donatedAtNs: Number(e.donatedAt),
					messageAtNs:
						Array.isArray(msgAt) && msgAt.length > 0 ? Number(msgAt[0]) : null,
				};
			});
			return { events, total: Number(page.total) };
		},
		staleTime: 15_000,
	});
}

export function useDonateGasIcp() {
	const { agent, ensureProtocolActor, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (icpAmount: number) => {
			if (!GAS_DONATE_PRESETS.includes(icpAmount as 1 | 5 | 10)) {
				throw new Error("Choose 1, 5, or 10 ICP");
			}
			const pair = await ensureProtocolActor();
			if (!pair || !agent) throw new Error("Connect Internet Identity");
			const config = getIcConfig();
			const icpId = config.ledgers.icp;
			if (!icpId) throw new Error("ICP ledger not configured");
			await approveIcrcSpender(
				pair.agent,
				icpId,
				config.protocolCanisterId,
				icpAmount,
			);
			const id = unwrapResult<bigint>(
				await pair.actor.donateGasIcp(amountToNat(icpAmount)),
				"donateGasIcp",
			);
			return { id: Number(id), icpAmount };
		},
		onSuccess: ({ icpAmount }) => {
			toast.success(`Donated ${icpAmount} ICP to Ops treasury`);
			void qc.invalidateQueries({ queryKey: ["v2", "gasSupporters"] });
			void qc.invalidateQueries({ queryKey: ["v2", "gasFunding"] });
			void qc.invalidateQueries({
				queryKey: ["v2", "public", "liquidityTreasury"],
			});
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(e.message),
	});
}

export function useSetGasDonationMessage() {
	const { ensureProtocolActor } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, message }: { id: number; message: string }) => {
			const pair = await ensureProtocolActor();
			if (!pair) throw new Error("Connect Internet Identity");
			const text = message.trim();
			if (!text) throw new Error("Message cannot be empty");
			if (text.length > 200) throw new Error("Message max 200 characters");
			unwrapResult(
				await pair.actor.setGasDonationMessage(BigInt(id), text),
				"setGasDonationMessage",
			);
		},
		onSuccess: () => {
			toast.success("Message posted to Supporters Wall");
			void qc.invalidateQueries({ queryKey: ["v2", "gasSupporters"] });
		},
		onError: (e: Error) => toast.error(e.message),
	});
}
